import { toast } from 'sonner';
import { ErrorLevel, ErrorNotificationPosition, type AppError } from './types';

export const DEFAULT_DURATION_MS = 5000;

/**
 * Handles displaying error notifications to users
 * Uses sonner toast library for UI notifications
 */
export class NotificationHandler {
  private static position: ErrorNotificationPosition = ErrorNotificationPosition.TOP_CENTER;
  private static duration = DEFAULT_DURATION_MS;

  /**
   * Configures global notification settings
   * @param options - Configuration options
   * @param options.position - Position on screen for notifications
   * @param options.duration - Duration in milliseconds to show notifications
   */
  static configure(options: { position?: ErrorNotificationPosition; duration?: number }) {
    if (options.position) {
      this.position = options.position;
    }
    if (options.duration) {
      this.duration = options.duration;
    }
  }

  /**
   * Displays an error notification based on error level
   * @param error - Application error object to display
   */
  static notify(error: AppError): void {
    const options = {
      position: this.position,
      duration: this.duration
    };

    switch (error.level) {
      case ErrorLevel.INFO:
        toast.info(error.message, options);
        break;
      case ErrorLevel.WARNING:
        toast.warning(error.message, options);
        break;
      case ErrorLevel.ERROR:
      case ErrorLevel.CRITICAL:
        toast.error(error.message, options);
        break;
      default:
        toast.error(error.message, options);
    }
  }

  /**
   * Shows a success notification
   * @param message - Success message to display
   */
  static success(message: string): void {
    toast.success(message, {
      position: this.position,
      duration: this.duration
    });
  }

  /**
   * Shows an info notification
   * @param message - Info message to display
   */
  static info(message: string): void {
    toast.info(message, {
      position: this.position,
      duration: this.duration
    });
  }

  /**
   * Shows a warning notification
   * @param message - Warning message to display
   */
  static warning(message: string): void {
    toast.warning(message, {
      position: this.position,
      duration: this.duration
    });
  }

  /**
   * Shows an error notification
   * @param message - Error message to display
   */
  static error(message: string): void {
    toast.error(message, {
      position: this.position,
      duration: this.duration
    });
  }
}
